import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Table as TableIcon,
  Mic, Square, Plus, Trash2, Save, StickyNote, Loader2, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

/* ---------- Toolbar for the ContentEditable editor ---------- */
function Toolbar({ exec, insertTable }) {
  const btn = "p-2 rounded-lg hover:bg-zen-subtle/25 text-zen-ink2 hover:text-zen-ink transition-colors";
  return (
    <div className="flex items-center gap-1 border-b border-zen-border pb-3 mb-4 flex-wrap" data-testid="editor-toolbar">
      <button type="button" title="Titolo grande" data-testid="tb-h1" onClick={()=>exec("formatBlock", "H1")} className={btn}><Heading1 size={16}/></button>
      <button type="button" title="Titolo medio" data-testid="tb-h2" onClick={()=>exec("formatBlock", "H2")} className={btn}><Heading2 size={16}/></button>
      <div className="w-px h-5 bg-zen-border mx-1"/>
      <button type="button" title="Grassetto" data-testid="tb-bold" onClick={()=>exec("bold")} className={btn}><Bold size={16}/></button>
      <button type="button" title="Corsivo" data-testid="tb-italic" onClick={()=>exec("italic")} className={btn}><Italic size={16}/></button>
      <button type="button" title="Sottolineato" data-testid="tb-underline" onClick={()=>exec("underline")} className={btn}><Underline size={16}/></button>
      <div className="w-px h-5 bg-zen-border mx-1"/>
      <button type="button" title="Elenco puntato" data-testid="tb-ul" onClick={()=>exec("insertUnorderedList")} className={btn}><List size={16}/></button>
      <button type="button" title="Elenco numerato" data-testid="tb-ol" onClick={()=>exec("insertOrderedList")} className={btn}><ListOrdered size={16}/></button>
      <div className="w-px h-5 bg-zen-border mx-1"/>
      <button type="button" title="Inserisci tabella 3x3" data-testid="tb-table" onClick={insertTable} className={btn}><TableIcon size={16}/></button>
    </div>
  );
}

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [title, setTitle] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const editorRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const loadNotes = async () => {
    const { data } = await api.get("/notes");
    setNotes(data);
    if (!selected && data.length > 0) openNote(data[0]);
    if (selected && !data.find(n => n.note_id === selected.note_id)) {
      setSelected(null); setTitle(""); if (editorRef.current) editorRef.current.innerHTML = "";
    }
  };
  useEffect(() => { loadNotes(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openNote = (n) => {
    setSelected(n);
    setTitle(n.title || "");
    if (editorRef.current) editorRef.current.innerHTML = n.content_html || "";
    setDirty(false);
  };

  const createNote = async () => {
    try {
      const { data } = await api.post("/notes", { title: "Nuovo", content_html: "" });
      setNotes([data, ...notes]);
      openNote(data);
      toast.success("Appunto creato");
      setTimeout(()=>editorRef.current?.focus(), 100);
    } catch (e) { toast.error(errMsg(e, "Errore")); }
  };

  const saveNote = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const content_html = editorRef.current?.innerHTML || "";
      const { data } = await api.put(`/notes/${selected.note_id}`, { title: title || "Appunto senza titolo", content_html });
      setNotes(notes.map(n => n.note_id === selected.note_id ? data : n));
      setSelected(data);
      setDirty(false);
      toast.success("Salvato");
    } catch (e) { toast.error(errMsg(e, "Errore")); }
    finally { setSaving(false); }
  };

  const deleteNote = async (n, e) => {
    e?.stopPropagation?.();
    if (!window.confirm(`Eliminare "${n.title}"?`)) return;
    await api.delete(`/notes/${n.note_id}`);
    const rest = notes.filter(x => x.note_id !== n.note_id);
    setNotes(rest);
    if (selected?.note_id === n.note_id) {
      if (rest.length > 0) openNote(rest[0]);
      else { setSelected(null); setTitle(""); if (editorRef.current) editorRef.current.innerHTML = ""; }
    }
  };

  /* ---- Editor commands ---- */
  const exec = (command, value = null) => {
    editorRef.current?.focus();
    // document.execCommand is deprecated but still works in all evergreen browsers and is
    // the pragmatic choice for a lightweight rich-text editor.
    document.execCommand(command, false, value);
    setDirty(true);
  };
  const insertTable = () => {
    const rows = 3, cols = 3;
    const style = "border-collapse:collapse;width:100%;margin:8px 0;";
    const cellStyle = "border:1px solid #E5E3D8;padding:8px;min-width:60px;";
    let html = `<table style="${style}"><tbody>`;
    for (let r = 0; r < rows; r++) {
      html += "<tr>";
      for (let c = 0; c < cols; c++) html += `<td style="${cellStyle}">&nbsp;</td>`;
      html += "</tr>";
    }
    html += "</tbody></table><p><br/></p>";
    exec("insertHTML", html);
  };

  const onEditorInput = () => setDirty(true);

  /* ---- Voice recording + Whisper transcription ---- */
  const startRecording = async () => {
    if (!selected) { toast.error("Seleziona o crea un appunto prima"); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Il tuo browser non supporta la registrazione audio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mime || "audio/webm" });
        await transcribeAndAppend(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      toast.info("Registrazione in corso — clicca 'Stop' quando hai finito.");
    } catch (e) {
      console.error("Microphone access failed", e);
      toast.error("Impossibile accedere al microfono. Autorizza il browser.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const transcribeAndAppend = async (blob) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("webm") ? "webm" : "wav";
      fd.append("file", blob, `recording.${ext}`);
      const { data } = await api.post("/notes/transcribe", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const text = (data.text || "").trim();
      if (!text) { toast.error("Nessun testo trascritto"); return; }
      // Insert transcript into the editor at caret (or append at end)
      const html = `<p><em>[Trascrizione audio]</em> ${text.replace(/\n/g, "<br/>")}</p>`;
      editorRef.current?.focus();
      if (!document.execCommand("insertHTML", false, html)) {
        editorRef.current.insertAdjacentHTML("beforeend", html);
      }
      setDirty(true);
      toast.success("Trascrizione inserita nell'appunto");
    } catch (e) {
      console.error("Transcription failed", e);
      toast.error(errMsg(e, "Trascrizione fallita"));
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="notes-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="zen-label">Appunti</span>
          <h1 className="mt-2 font-heading text-3xl">Note & Trascrizioni</h1>
          <p className="text-zen-ink2 mt-1">Scrivi, elenca, tabella, o registra una call — l&apos;AI trascrive per te.</p>
        </div>
        <Button data-testid="new-note-btn" onClick={createNote} className="bg-zen-primary hover:bg-zen-primaryHover rounded-full">
          <Plus size={16} className="mr-1"/> Nuovo
        </Button>
      </div>

      <div className="zen-card overflow-hidden">
        <div className="grid md:grid-cols-[300px_1fr] min-h-[560px]">
          {/* Left: notes list */}
          <div className="border-r border-zen-border max-h-[720px] overflow-y-auto">
            {notes.length === 0 ? (
              <div className="p-8 text-center text-zen-ink2 text-sm">
                <StickyNote size={24} className="mx-auto mb-2 text-zen-subtle"/>
                Nessun appunto. Creane uno per iniziare.
              </div>
            ) : notes.map((n) => (
              <div
                key={n.note_id}
                role="button"
                tabIndex={0}
                data-testid={`note-item-${n.note_id}`}
                onClick={()=>openNote(n)}
                onKeyDown={(e)=>{ if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openNote(n); } }}
                className={`w-full text-left p-4 border-b border-zen-border hover:bg-zen-bg transition-colors flex items-start gap-3 group cursor-pointer focus:outline-none focus:bg-zen-bg ${selected?.note_id === n.note_id ? "bg-zen-bg" : ""}`}
              >
                <FileText size={15} className="mt-0.5 text-zen-primary shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zen-ink truncate">{n.title}</div>
                  <div className="text-xs text-zen-ink2 mt-0.5 truncate">{fmtDate(n.updated_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={(e)=>deleteNote(n, e)}
                  data-testid={`del-note-${n.note_id}`}
                  className="text-zen-alert opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}
          </div>

          {/* Right: editor */}
          <div className="p-6 md:p-8 flex flex-col">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-zen-ink2 text-center">
                <div>
                  <StickyNote size={36} className="mx-auto mb-3 text-zen-subtle"/>
                  <p>Seleziona un appunto o creane uno nuovo.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5 flex-wrap">
                  <Input
                    data-testid="note-title"
                    value={title}
                    onChange={(e)=>{ setTitle(e.target.value); setDirty(true); }}
                    placeholder="Titolo dell'appunto…"
                    className="text-lg font-heading flex-1 min-w-0 border-none shadow-none focus-visible:ring-0 px-0"
                  />
                  <div className="flex items-center gap-2">
                    {!recording ? (
                      <Button
                        data-testid="rec-start"
                        type="button" variant="outline" onClick={startRecording}
                        disabled={transcribing}
                        className="rounded-full text-zen-primary border-zen-primary/30 hover:bg-zen-primary/5"
                      >
                        {transcribing ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Mic size={14} className="mr-1.5"/>}
                        {transcribing ? "Trascrivo…" : "Registra"}
                      </Button>
                    ) : (
                      <Button
                        data-testid="rec-stop"
                        type="button" onClick={stopRecording}
                        className="rounded-full bg-zen-alert hover:bg-zen-alert/90 text-white animate-pulse"
                      >
                        <Square size={12} className="mr-1.5 fill-current"/> Stop
                      </Button>
                    )}
                    <Button
                      data-testid="save-note"
                      type="button" onClick={saveNote}
                      disabled={saving || !dirty}
                      className="bg-zen-primary hover:bg-zen-primaryHover text-white rounded-full"
                    >
                      <Save size={14} className="mr-1.5"/> {saving ? "…" : dirty ? "Salva" : "Salvato"}
                    </Button>
                  </div>
                </div>

                <Toolbar exec={exec} insertTable={insertTable}/>

                <div
                  ref={editorRef}
                  data-testid="note-editor"
                  contentEditable
                  suppressContentEditableWarning
                  onInput={onEditorInput}
                  className="flex-1 min-h-[400px] outline-none prose prose-sm max-w-none text-zen-ink leading-relaxed [&_h1]:font-heading [&_h1]:text-2xl [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:font-heading [&_h2]:text-xl [&_h2]:mt-3 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_table]:my-3 [&_p]:my-1"
                  style={{ whiteSpace: "pre-wrap" }}
                />

                <div className="mt-4 pt-3 border-t border-zen-border text-xs text-zen-ink2 flex items-center gap-4 flex-wrap">
                  <span>Ultima modifica: {fmtDate(selected.updated_at)}</span>
                  {dirty && <span className="text-zen-alert">● modifiche non salvate</span>}
                  {transcribing && <span className="text-zen-primary flex items-center gap-1"><Loader2 size={12} className="animate-spin"/> AI sta trascrivendo…</span>}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
